import { useMemo } from "react";
import { Link } from "wouter";
import { useDatabase } from "@/hooks/useDatabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Sparkles, TrendingUp, Target, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Marketing() {
  const { database, loading, error } = useDatabase();

  const marketingData = useMemo(() => {
    if (!database) return { heroStats: [], topStudies: [], byMushroom: {} };

    // Get all marketing angles with study info
    const heroStats = database.marketing_angles
      .filter(m => m.social_media_ready)
      .map(m => {
        const study = database.studies.find(s => s.study_id === m.study_id);
        const intervention = database.interventions.find(i => i.study_id === m.study_id);
        const mushroom = intervention ? database.mushrooms.find(mu => mu.mushroom_id === intervention.mushroom_id) : null;
        return { ...m, study, mushroom };
      })
      .sort((a, b) => (b.study?.quality_score || 0) - (a.study?.quality_score || 0));

    // Top studies by quality
    const topStudies = database.studies
      .sort((a, b) => b.quality_score - a.quality_score)
      .slice(0, 5)
      .map(study => {
        const marketing = database.marketing_angles.find(m => m.study_id === study.study_id);
        const intervention = database.interventions.find(i => i.study_id === study.study_id);
        const mushroom = intervention ? database.mushrooms.find(m => m.mushroom_id === intervention.mushroom_id) : null;
        return { study, marketing, mushroom };
      });

    // Group by mushroom
    const byMushroom: Record<string, any[]> = {};
    database.mushrooms.forEach(mushroom => {
      const mushroomStudies = database.interventions
        .filter(i => i.mushroom_id === mushroom.mushroom_id)
        .map(i => {
          const study = database.studies.find(s => s.study_id === i.study_id);
          const marketing = database.marketing_angles.find(m => m.study_id === i.study_id);
          return { study, marketing };
        })
        .filter(item => item.study && item.marketing);
      
      if (mushroomStudies.length > 0) {
        byMushroom[mushroom.common_name] = mushroomStudies;
      }
    });

    return { heroStats, topStudies, byMushroom };
  }, [database]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="text-center">
          <Sparkles className="w-16 h-16 mx-auto mb-4 text-amber-600 animate-pulse" />
          <p className="text-lg text-gray-600">Loading marketing dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100">
        <div className="text-center">
          <p className="text-lg text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-[#F4F2EC]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" className="gap-2 mb-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Database
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Marketing Dashboard</h1>
              <p className="text-sm text-gray-600">Social Media Ready Content & Hero Stats</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Hero Stats Section */}
        <Card className="mb-8 border-amber-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Hero Stats - Social Media Ready
            </CardTitle>
            <CardDescription>
              High-impact statistics perfect for social media posts and marketing materials
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {marketingData.heroStats.map((item, index) => (
                <div key={item.marketing_id} className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                          {item.mushroom?.common_name || "Unknown"}
                        </Badge>
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                          {item.study?.year}
                        </Badge>
                        <span className="text-sm">{"⭐".repeat(item.study?.quality_score || 0)}</span>
                      </div>
                      <p className="text-xl font-bold text-amber-900 mb-2">{item.hero_stat}</p>
                      <p className="text-sm text-gray-700 mb-2">{item.story_hook}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {item.content_tags?.map((tag: string) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(item.hero_stat, "Hero stat")}
                        className="gap-2"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </Button>
                      <Link href={`/study/${item.study_id}`}>
                        <Button size="sm" variant="outline">
                          View Study
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Quality Studies */}
        <Card className="mb-8 border-amber-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top Quality Studies
            </CardTitle>
            <CardDescription>
              Highest quality research for credibility and authority content
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {marketingData.topStudies.map((item, index) => (
                <div key={item.study.study_id} className="p-4 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full text-amber-700 font-bold text-xl">
                      #{index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-[#F4F2EC] text-[#2A2A28] border-[#E4E1D8]">
                          {item.mushroom?.common_name || "Unknown"}
                        </Badge>
                        <span className="text-lg">{"⭐".repeat(item.study.quality_score)}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{item.study.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{item.study.key_finding_summary}</p>
                      {item.marketing && (
                        <p className="text-sm text-amber-800 bg-amber-50 p-2 rounded">
                          💡 {item.marketing.hero_stat}
                        </p>
                      )}
                    </div>
                    <Link href={`/study/${item.study.study_id}`}>
                      <Button size="sm">View</Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* By Mushroom Species */}
        <Card className="mb-8 border-amber-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Content by Mushroom Species
            </CardTitle>
            <CardDescription>
              Organized marketing angles for species-specific campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.entries(marketingData.byMushroom).map(([mushroomName, studies]) => (
                <div key={mushroomName}>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-2 h-8 bg-amber-500 rounded"></span>
                    {mushroomName}
                    <Badge variant="outline" className="ml-2">{studies.length} studies</Badge>
                  </h3>
                  <div className="grid gap-3 ml-4">
                    {studies.map((item: any) => (
                      <div key={item.study.study_id} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 mb-1">{item.marketing.hero_stat}</p>
                            <p className="text-sm text-gray-600">{item.study.title}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(item.marketing.hero_stat, "Stat")}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Content Creation Tips */}
        <Card className="border-amber-200 shadow-lg">
          <CardHeader>
            <CardTitle>Content Creation Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-muted rounded-lg border border-border">
                <p className="font-semibold text-foreground mb-1">📊 Use Specific Numbers</p>
                <p className="text-muted-foreground">Percentages and p-values add credibility. Always include sample size (n=X).</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="font-semibold text-green-900 mb-1">🎯 Lead with Benefits</p>
                <p className="text-green-800">Start with the outcome, then mention the mushroom. "38% immune boost" before "from Cordyceps".</p>
              </div>
              <div className="p-3 bg-[#F4F2EC] rounded-lg border border-[#E4E1D8]">
                <p className="font-semibold text-[#2A2A28] mb-1">⭐ Highlight Quality</p>
                <p className="text-[#8A6F22]">Mention RCTs, peer-reviewed journals, and recent publication dates for authority.</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="font-semibold text-orange-900 mb-1">✅ Safety Matters</p>
                <p className="text-orange-800">Zero adverse events is a powerful selling point. Always mention safety profile.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
